import React from 'react';

interface FortuneRatingsProps {
  ratings: {
    overall: number;
    romance: number;
    health: number;
    money: number;
  };
  locale?: 'EN' | 'JA';
}

interface StarRatingProps {
  rating: number;
  label: string;
}

const StarRating: React.FC<StarRatingProps> = ({ rating, label }) => {
  const filledStars = Math.min(Math.max(rating, 0), 5);

  return (
    <div className="flex items-center justify-between py-1">
      <span
        className="text-gray-800 font-medium flex-1"
        style={{ fontSize: 'clamp(0.8rem, 3vw, 0.85rem)' }}
      >
      </span>
      <div className="flex items-center gap-0">
        {[1, 2, 3, 4, 5].map((starIndex) => {
          const isFilled = starIndex <= filledStars;
          const isPartial = starIndex === Math.ceil(filledStars) && filledStars % 1 !== 0;

          return (
            <div
              key={starIndex}
              className="relative w-5 h-5"
              style={{ filter: 'drop-shadow(0 1px 1px rgba(0, 0, 0, 0.1))' }}
            >
              {/* Background star (empty) */}
              <svg
                className="absolute inset-0 w-full h-full text-gray-100"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>

              {/* Filled star */}
              {isFilled && (
                <svg
                  className="absolute inset-0 w-full h-full"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  style={{
                    color: '#cdbe8e',
                    clipPath: isPartial
                      ? `polygon(0 0, ${(filledStars % 1) * 100}% 0, ${(filledStars % 1) * 100}% 100%, 0 100%)`
                      : 'none'
                  }}
                >
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const FortuneRatings: React.FC<FortuneRatingsProps> = ({ ratings, locale = 'JA' }) => {
  const ratingLabels = {
    JA: {
      overall: '総合運',
      romance: '恋愛運',
      health: '健康運',
      money: '金運'
    },
    EN: {
      overall: 'Overall',
      romance: 'Romance',
      health: 'Health',
      money: 'Money'
    }
  };

  const backgroundImagePath = `/img/omikuji/Omikuji_UI_${locale}_STATS.png`;

  return (
    <div
      className="relative"
      style={{
        backgroundImage: `url(${backgroundImagePath})`,
        backgroundSize: 'contain',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        aspectRatio: '440 / 320', // Adjust based on actual stats image dimensions
        minHeight: '150px'
      }}
    >
      <div className="absolute inset-0 flex flex-col justify-center">
        <div className="space-y-0 px-3 pt-1">
          <StarRating rating={ratings.overall} label={ratingLabels[locale].overall} />
          <StarRating rating={ratings.romance} label={ratingLabels[locale].romance} />
          <StarRating rating={ratings.health} label={ratingLabels[locale].health} />
          <StarRating rating={ratings.money} label={ratingLabels[locale].money} />
        </div>
      </div>
    </div>
  );
};